import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';

import {
  isChunkError,
  recentlyReloaded,
  markReload,
  clearStaleMark,
} from '../utils/chunkReload';

class ChunkErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, chunk: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error, chunk: isChunkError(error) };
  }

  componentDidMount() {
    clearStaleMark();
  }

  componentDidCatch(error, info) {
    // Keep the error visible in the console for support / Sentry-free debugging.
    // eslint-disable-next-line no-console
    console.error('[ChunkErrorBoundary]', error, info);

    if (isChunkError(error) && !recentlyReloaded()) {
      markReload();
      // Small delay so React can flush the boundary render before navigating.
      setTimeout(() => window.location.reload(), 50);
    }
  }

  handleReload = () => {
    markReload();
    window.location.reload();
  };

  renderFallback() {
    const { error, chunk } = this.state;

    if (chunk && !recentlyReloaded()) {
      return (
        <Box sx={{ p: 4, textAlign: 'center', maxWidth: 480, mx: 'auto', mt: 8 }}>
          <Typography variant="body1">Reloading…</Typography>
        </Box>
      );
    }

    if (chunk) {
      return (
        <Box sx={{ p: 4, textAlign: 'center', maxWidth: 480, mx: 'auto', mt: 8 }}>
          <ReportProblemOutlinedIcon sx={{ fontSize: 48, color: '#d32f2f', mb: 1 }} />
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            Couldn't load the page
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            A part of the application failed to load. Check your internet connection and try again.
          </Typography>
          <Button variant="contained" onClick={this.handleReload}>Reload</Button>
        </Box>
      );
    }

    return (
      <Box sx={{ p: 4, textAlign: 'center', maxWidth: 520, mx: 'auto', mt: 6 }}>
        <ReportProblemOutlinedIcon sx={{ fontSize: 48, color: '#d32f2f', mb: 1 }} />
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          Something went wrong
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          The page encountered an unexpected error. Please reload, and if it keeps happening — send a screenshot to support.
        </Typography>
        {process.env.NODE_ENV !== 'production' && error && (
          <Typography
            variant="caption"
            component="pre"
            sx={{
              textAlign: 'left',
              whiteSpace: 'pre-wrap',
              background: '#fafafa',
              p: 2,
              borderRadius: 1,
              mb: 2,
              maxHeight: 320,
              overflow: 'auto',
              fontSize: 11,
            }}
          >
            {String(error.stack || error)}
          </Typography>
        )}
        <Button variant="contained" onClick={this.handleReload}>Reload</Button>
      </Box>
    );
  }

  render() {
    if (this.state.hasError) return this.renderFallback();
    return this.props.children;
  }
}

export default ChunkErrorBoundary;
